import { Moment } from 'moment';
// Types
import {
  CreateTicketEventInput,
  RescheduleTicketEventInput,
  TicketEventStatus,
  TicketTierDetailsInput,
  TicketVenueDetailsInput,
  UpdateTicketEventDetailsInput,
} from 'api/graphql-global-types';
import { TicketEventDetailsFields } from 'api/ticketing/types/TicketEventDetailsFields';
import {
  PublishMode,
  TicketEventFormValues,
  TierFormValues,
  VenueFormValues,
  VenueMode,
} from './types';
// Helpers
import { fromVenueInstant, toVenueInstant } from 'pages/Events/helpers';
import { mapBoutsToMatchupsInput } from 'pages/Events/matchcard';

export const DEFAULT_MAX_PER_ORDER = 10;

/** Empty text is "not set" to the backend, never an empty string. */
export const toOptionalText = (value?: string | null): string | null => {
  const trimmed = value?.trim();

  return trimmed ? trimmed : null;
};

const toOptionalInstant = (
  value: Moment | null | undefined,
  timezone: string
): string | null => (value ? toVenueInstant(value, timezone) : null);

export const buildVenueInput = (
  venue: VenueFormValues
): TicketVenueDetailsInput => ({
  name: venue.name?.trim() || '',
  addressLine1: venue.addressLine1?.trim() || '',
  addressLine2: toOptionalText(venue.addressLine2),
  city: venue.city?.trim() || '',
  region: venue.region?.trim() || '',
  postalCode: venue.postalCode?.trim() || '',
  country: (venue.country || '').trim().toUpperCase(),
  latitude: Number(venue.latitude),
  longitude: Number(venue.longitude),
  timezone: venue.timezone || '',
});

export const buildTierInput = (
  tier: TierFormValues
): TicketTierDetailsInput => ({
  label: tier.label?.trim() || '',
  price: Number(tier.price),
  capacity: Number(tier.capacity),
  maxPerOrder: tier.maxPerOrder ?? DEFAULT_MAX_PER_ORDER,
  description: toOptionalText(tier.description),
});

export const getTierCapacityTotal = (
  tiers: { capacity?: number | null }[] = []
): number =>
  tiers.reduce((total, tier) => total + (Number(tier.capacity) || 0), 0);

/** The event cap, raised when it is unset or too low for the tiers. */
export const fitMaxAdmissions = (
  maxAdmissions: number | null | undefined,
  neededCapacity: number
): number => Math.max(maxAdmissions || 0, neededCapacity);

/** Every value the admin left empty is omitted so the backend applies its default. */
export const buildCreateTicketEventInput = (
  values: TicketEventFormValues,
  {
    timezone,
    coverImageFileKey,
  }: { timezone: string; coverImageFileKey?: string | null }
): CreateTicketEventInput => {
  const tiers = values.ticketsEnabled ? values.tiers || [] : [];
  const input: CreateTicketEventInput = {
    ownerId: values.hostId,
    name: toOptionalText(values.name),
    description: toOptionalText(values.description),
    hashtagInputs: (values.hashtags || []).map((name) => ({ name })),
    startsAt: toOptionalInstant(values.startsAt, timezone),
    endsAt: toOptionalInstant(values.endsAt, timezone),
    doorsOpenAt: toOptionalInstant(values.doorsOpenAt, timezone),
    admissionClosesAt: toOptionalInstant(values.admissionClosesAt, timezone),
    salesOpenAt: toOptionalInstant(values.salesOpenAt, timezone),
    salesCloseAt: toOptionalInstant(values.salesCloseAt, timezone),
    repeatsEvery: values.repeatsEvery || null,
    ticketsEnabled: values.ticketsEnabled,
    tiers: values.ticketsEnabled ? tiers.map(buildTierInput) : null,
    maxAdmissions: values.ticketsEnabled ? values.maxAdmissions || null : null,
    minimumAge: values.minimumAge || null,
    promotion: toOptionalText(values.promotion),
    entryRequirements: toOptionalText(values.entryRequirements),
    refundPolicy: toOptionalText(values.refundPolicy),
    termsAndConditions: toOptionalText(values.termsAndConditions),
    sportIds: values.sportIds?.length ? values.sportIds : null,
    leagueId: values.leagueId || null,
    affiliateEnabled: values.affiliateEnabled,
    affiliateCommissionPercent: values.affiliateEnabled
      ? values.affiliateCommissionPercent ?? null
      : null,
    fanPollingEnabled: values.fanPollingEnabled,
    coverImageFileKey: coverImageFileKey || null,
  };

  if (values.venueMode === VenueMode.Saved) {
    input.venueId = values.venueId;
  } else {
    input.venue = buildVenueInput(values.venue || {});
  }

  // A stream-linked event takes these from the stream and refuses its own
  if (values.streamId) {
    input.streamId = values.streamId;
    input.repeatsEvery = null;
    input.affiliateEnabled = null;
    input.affiliateCommissionPercent = null;
    input.fanPollingEnabled = null;
  } else {
    const matchups = mapBoutsToMatchupsInput(values.bouts);
    input.matchups = matchups.length ? matchups : null;
  }

  return Object.fromEntries(
    Object.entries(input).filter(([, value]) => value !== null)
  ) as CreateTicketEventInput;
};

type EventDetails = TicketEventDetailsFields;

export const getEditFormValues = (
  event: EventDetails
): TicketEventFormValues => {
  const timezone = event.timezone;
  const toPicker = (value: string | null) =>
    value ? fromVenueInstant(value, timezone) : null;

  return {
    hostId: event.hostUserId,
    name: event.name,
    description: event.description || '',
    hashtags: event.hashtags.map(({ name }) => name),
    venueMode: VenueMode.Saved,
    venueId: event.venueId || undefined,
    startsAt: toPicker(event.startsAt),
    endsAt: toPicker(event.endsAt),
    doorsOpenAt: toPicker(event.doorsOpenAt),
    admissionClosesAt: toPicker(event.admissionClosesAt),
    salesOpenAt: toPicker(event.salesOpenAt),
    salesCloseAt: toPicker(event.salesCloseAt),
    repeatsEvery: event.repeatsEvery,
    ticketsEnabled: event.ticketsEnabled,
    tiers: [],
    maxAdmissions: event.maxAdmissions,
    minimumAge: event.minimumAge,
    promotion: event.promotion || '',
    entryRequirements: event.entryRequirements || '',
    refundPolicy: event.refundPolicy || '',
    termsAndConditions: event.termsAndConditions || '',
    sportIds: event.sports.map(({ id }) => id),
    leagueId: event.league?.id || null,
    affiliateEnabled: event.affiliateEnabled,
    affiliateCommissionPercent: event.affiliateCommissionPercent,
    fanPollingEnabled: event.fanPollingEnabled,
    publishMode: PublishMode.Draft,
    approveZeroTax: false,
  };
};

const sameList = <T>(a: T[] = [], b: T[] = []): boolean =>
  a.length === b.length && a.every((item, index) => item === b[index]);

/** Only changed fields, so an edit never trips a rule on an untouched field; null when nothing changed. */
export const buildUpdateDetailsInput = (
  event: EventDetails,
  values: TicketEventFormValues,
  { coverImageFileKey }: { coverImageFileKey?: string | null } = {}
): UpdateTicketEventDetailsInput | null => {
  const initial = getEditFormValues(event);
  const changes: Partial<UpdateTicketEventDetailsInput> = {};
  const textFields = [
    'description',
    'promotion',
    'entryRequirements',
    'refundPolicy',
    'termsAndConditions',
  ] as const;

  if (values.name?.trim() !== event.name) {
    changes.name = values.name?.trim();
  }

  textFields.forEach((field) => {
    if (toOptionalText(values[field]) !== toOptionalText(initial[field])) {
      changes[field] = toOptionalText(values[field]);
    }
  });

  if (!sameList(values.hashtags, initial.hashtags)) {
    changes.hashtagInputs = (values.hashtags || []).map((name) => ({ name }));
  }

  if (!sameList(values.sportIds, initial.sportIds)) {
    changes.sportIds = values.sportIds || [];
  }

  if ((values.leagueId || null) !== initial.leagueId) {
    changes.leagueId = values.leagueId || null;
  }

  if ((values.repeatsEvery || null) !== (initial.repeatsEvery || null)) {
    changes.repeatsEvery = values.repeatsEvery || null;
  }

  if ((values.minimumAge || null) !== (initial.minimumAge || null)) {
    changes.minimumAge = values.minimumAge || null;
  }

  if (values.ticketsEnabled !== initial.ticketsEnabled) {
    changes.ticketsEnabled = values.ticketsEnabled;
  }

  if (
    values.ticketsEnabled &&
    values.maxAdmissions &&
    values.maxAdmissions !== initial.maxAdmissions
  ) {
    changes.maxAdmissions = values.maxAdmissions;
  }

  if (
    values.venueMode === VenueMode.Saved &&
    values.venueId &&
    values.venueId !== event.venueId
  ) {
    changes.venueId = values.venueId;
  }

  if (values.affiliateEnabled !== initial.affiliateEnabled) {
    changes.affiliateEnabled = values.affiliateEnabled;
  }

  if (
    values.affiliateEnabled &&
    (values.affiliateCommissionPercent ?? null) !==
      (initial.affiliateCommissionPercent ?? null)
  ) {
    changes.affiliateCommissionPercent =
      values.affiliateCommissionPercent ?? null;
  }

  if (values.fanPollingEnabled !== initial.fanPollingEnabled) {
    changes.fanPollingEnabled = values.fanPollingEnabled;
  }

  if (coverImageFileKey) {
    changes.coverImageFileKey = coverImageFileKey;
  }

  if (!Object.keys(changes).length) {
    return null;
  }

  return {
    ticketEventId: event.id,
    expectedVersion: event.version,
    ...changes,
  };
};

type ScheduleField =
  | 'startsAt'
  | 'endsAt'
  | 'doorsOpenAt'
  | 'admissionClosesAt'
  | 'salesOpenAt'
  | 'salesCloseAt';

const sameInstant = (a: string | null, b: string | null): boolean =>
  a === b || (!!a && !!b && new Date(a).getTime() === new Date(b).getTime());

/**
 * Reschedule takes the whole schedule at once. Returns null when no date
 * changed, so a details-only edit doesn't reopen a postponed event.
 */
export const buildRescheduleInput = (
  event: EventDetails,
  values: TicketEventFormValues,
  expectedVersion: number,
  timezone: string = event.timezone
): RescheduleTicketEventInput | null => {
  // Pickers hold clock times, so a new venue zone moves every date
  const zoneChanged = timezone !== event.timezone;
  const toInstant = (key: ScheduleField): string | null => {
    const stored = event[key];
    const value =
      values[key] ||
      (zoneChanged && stored ? fromVenueInstant(stored, event.timezone) : null);

    return toOptionalInstant(value, timezone);
  };
  const next = {
    startsAt: toInstant('startsAt'),
    endsAt: toInstant('endsAt'),
    doorsOpenAt: toInstant('doorsOpenAt'),
    admissionClosesAt: toInstant('admissionClosesAt'),
    salesOpenAt: toInstant('salesOpenAt'),
    salesCloseAt: toInstant('salesCloseAt'),
  };

  // A cleared picker keeps the stored time (see the fallbacks below), so it is no change
  const changed =
    zoneChanged ||
    (Object.keys(next) as (keyof typeof next)[]).some(
      (key) => next[key] !== null && !sameInstant(next[key], event[key])
    );

  if (!changed) {
    return null;
  }

  return {
    ticketEventId: event.id,
    expectedVersion,
    startsAt: next.startsAt || event.startsAt,
    endsAt: next.endsAt || event.endsAt,
    doorsOpenAt: next.doorsOpenAt || event.doorsOpenAt,
    admissionClosesAt: next.admissionClosesAt || event.admissionClosesAt,
    salesCloseAt: next.salesCloseAt || event.salesCloseAt,
    salesOpenAt: next.salesOpenAt || event.salesOpenAt,
  };
};

export const SCHEDULE_FIELDS_FOLLOWING_START = [
  'endsAt',
  'doorsOpenAt',
  'admissionClosesAt',
  'salesCloseAt',
] as const;

type ScheduleValues = Partial<Record<ScheduleField, Moment | null>>;

/** Moves the times that follow the start by the same amount. */
export const getShiftedSchedule = (
  values: ScheduleValues,
  deltaMs: number
): Partial<Record<ScheduleField, Moment>> =>
  SCHEDULE_FIELDS_FOLLOWING_START.reduce<
    Partial<Record<ScheduleField, Moment>>
  >((shifted, field) => {
    const value = values[field];

    return value && deltaMs
      ? { ...shifted, [field]: value.clone().add(deltaMs, 'ms') }
      : shifted;
  }, {});

export const isVenueLocked = (status?: TicketEventStatus): boolean =>
  !!status && status !== TicketEventStatus.Draft;

/** Describes an offset from the event start, e.g. "2 hours before the start". */
export const formatHours = (seconds: number): string => {
  const hours = Math.round((seconds / 3600) * 100) / 100;

  if (hours === 0) {
    return 'at the start time';
  }

  return `${Math.abs(hours)} hour${Math.abs(hours) === 1 ? '' : 's'} ${
    hours > 0 ? 'after' : 'before'
  } the start`;
};
